import { useHistory, useLocation } from "react-router-dom";

import { identity } from "~/tools/identity";
import { useHandler } from "~/tools/use-handler";

import { useQueryViews } from "../../../../common/use-query-views";
import { timeTransitionMiddleware } from "../_time-transition-middleware";
import { type NavigateToOptions, navigateToLink } from "../core";
import { applyViewDiffMiddleware } from "../middlewares";
import type { HistoryOptions } from "../types";

/**
 * Use it when the target location does not depend on the current location and when all params are known.
 * For example when it needs to redirect from a route to another route.
 *
 * ---
 *
 * A function to navigate to the given route.
 *
 * If the options.params is a function, the argument might be `null` when the route is not matched with the current location.
 *
 * If the route has params specified as "path/:param1/:param2", the values of the params are passed to the `options.params`.
 * If the route has no params, or params are optional, the `options.params` is not required.
 *
 * The search might be modified via `options.search`. If the `options.search` is not defined, the search will be empty.
 *
 * @param route - a route to navigate to. If it matches current location the params will pass to the `options.params` function.
 * @param options - options to modify the route params and search.
 *
 * @example
 * const link = useNavigateToLink("path/:param1?/:param2(a|b)", {
 *   params: { param1: "value1", param2: "a" },
 *   search: { search1: "value1", search2: "value2" },
 * });
 * // === "path/value1/a?search1=value1&search2=value2"
 *
 * const linkFromParams = useNavigateToLink("path/:param1/:param2?", {
 *   params: (current) => ({ ...current, param1: "value1" }), // keeps the value of param2 if any
 *   search: search => search
 * });
 * // === "path/value1
 * // or if the current location is "path/value1/a?search1=value1&search2=value2"
 * // === "path/value1/a?search1=value1&search2=value2"
 */
export type NavigateToLink<TReturn = string, TExtraOptions = unknown> = <TRoute extends string>(
	route: TRoute,
	options: TExtraOptions & NavigateToOptions<TRoute>,
) => TReturn;

/**
 * A hook that implements `NavigateToLink<string>`.
 *
 * @see NavigateToLink
 */
export const useNavigateToLink: NavigateToLink = (route, options) => {
	const location = useLocation();
	const views = useQueryViews();

	return navigateToLink(
		{
			location,
			middleware: [views ? applyViewDiffMiddleware(views) : identity, timeTransitionMiddleware],
		},
		route,
		options,
	);
};

/**
 * A hook returns `NavigateToLink<string>` function.
 *
 * @returns NavigateToLink<string>
 * @see NavigateToLink
 */
export const useNavigateToLinkLazy = (): NavigateToLink => {
	const history = useHistory();
	const views = useQueryViews();

	return (route, options) =>
		navigateToLink(
			{
				location: history.location,
				middleware: [views ? applyViewDiffMiddleware(views) : identity, timeTransitionMiddleware],
			},
			route,
			options,
		);
};

/**
 * A hook for navigating to the given route.
 *
 * @returns NavigateToLink<void>
 * @see NavigateToLink
 */
export const useNavigateTo = (): NavigateToLink<void, HistoryOptions> => {
	const history = useHistory();
	const navigateToLinkLazy = useNavigateToLinkLazy();

	return useHandler(
		<TRoute extends string>(
			route: TRoute,
			{ method = "push", ...options }: HistoryOptions & NavigateToOptions<TRoute>,
		) => {
			const url = navigateToLinkLazy(route, options as NavigateToOptions<TRoute>);

			if (url === `${history.location.pathname}${history.location.search}`) {
				return;
			}

			if (method === "push") {
				history.push(url);
			} else {
				history.replace(url);
			}
		},
	);
};
